import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import useAuth from '../hooks/useAuth';
import { getReviewQueue, getMentorStats } from '../services/mentorApi';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queueCount, setQueueCount] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.mentor_id) return;
    setLoading(true);
    Promise.all([
      getReviewQueue(user.mentor_id, 1).catch(() => ({ count: 0 })),
      getMentorStats(user.mentor_id).catch(() => null),
    ])
      .then(([queue, statsData]) => {
        setQueueCount(queue?.count ?? 0);
        setStats(statsData);
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review submitted assessments and approve reports for client delivery.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <StatCard
              icon={<HourglassEmptyIcon sx={{ fontSize: 32, color: '#d97706' }} />}
              label="Pending in Queue"
              value={queueCount}
              color="#d97706"
            />
            <StatCard
              icon={<CheckCircleIcon sx={{ fontSize: 32, color: '#16a34a' }} />}
              label="Approved (all time)"
              value={stats?.approved_count ?? '—'}
              color="#16a34a"
            />
            <StatCard
              icon={<RateReviewIcon sx={{ fontSize: 32, color: '#2563eb' }} />}
              label="Total Reviewed"
              value={stats?.total_reviewed ?? '—'}
              color="#2563eb"
            />
          </Grid>

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Review Queue
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {queueCount === 0
                    ? 'No assessments waiting. Nice work.'
                    : `${queueCount} assessment${queueCount === 1 ? '' : 's'} waiting for review.`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<RateReviewIcon />}
                onClick={() => navigate('/queue')}
                disabled={queueCount === 0}
              >
                Open Queue
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <Grid item xs={12} sm={4}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box sx={{ flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color,
                fontSize: 'clamp(1.05rem, 4vw, 2.125rem)',
                lineHeight: 1.2,
                overflow: 'hidden',
                wordBreak: 'break-word',
                
              }}
              title={String(value ?? '—')}
            >
              {value ?? '—'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}
